import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, Fragment, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { createUseStyles, Button, Section, Tooltip, Icon, Link, Text } from 'hoi-poi-ui';
import ModalOptions from "../ModalOptions";
import FilterViews from "../FilterViews";
import { getCrossID } from "../../utils/filters";
import { isEqual } from "../../utils/objects";
import { useWeb5Context } from "../../lib/Web5Context";
import { FiltersContext } from "./Context";
import Empty from "./Empty";
import { defaultValues, inputFields, rangeFields } from "./constants";
import { operators, fieldOperators } from "./operators";
import FiltersSection from "./FiltersSection";
import useFilters from "./hooks/useFilters";
import styles from "./styles";
const {
  EMPTY,
  NOT_EMPTY,
  GREATER,
  GREATER_OR_EQUAL,
  LESS,
  LESS_OR_EQUAL,
  INSIDE_RANGE,
  OUTSIDE_RANGE,
  NO_DATA_WITHIN_RANGE
} = operators;
const useStyles = createUseStyles(styles, 'FiltersContent');
const getItemStyle = (_, draggableStyle) => _objectSpread(_objectSpread({
  userSelect: 'none',
  marginBottom: 8
}, draggableStyle), {}, {
  left: 24,
  cursor: 'auto'
});
var _ref7 = /*#__PURE__*/React.createElement(Empty, null);
const Content = /*#__PURE__*/memo(_ref => {
  let {
    quickSchema = [],
    schema,
    extraSchema,
    showAdd,
    className,
    onChange,
    values,
    labels,
    labelsViews,
    labelsSaveViewsModal,
    labelsDeleteViewsModal,
    onToggle,
    toggled = {},
    onDelete,
    onToggleSaveViewsModal,
    isOpenViewsModal,
    useViewsInFilters,
    useViewsInMenu,
    views,
    canManagePublicViews,
    canManagePrivateViews,
    selectedView,
    customView,
    onChangeView,
    onChangeCustomView,
    onSaveView,
    onDeleteView,
    onDeleteCustomView,
    selectedFilters,
    onChangeSelectedFilters,
    onToggleAddModal,
    toggleQuickFilter,
    onReOrder,
    avoidQuickFilters,
    onDrag,
    onDrop,
    operators,
    onChangeOperator,
    crossSchema,
    crossOperators,
    extraMultiSchema,
    entity,
    useGeneralSections = true,
    useSectionsTitle = false,
    getAllowedOperators = () => {},
    getHasAdvancedFilters = () => {},
    getFieldsWithoutAdvancedFilters = () => {},
    useSections = true,
    isPinnable = true,
    modalOptionsProps,
    useHeader
  } = _ref;
  const classes = useStyles();
  const classNames = classnames(classes.content, className);
  const [innerValues, setInnerValues] = useState(values || {});
  const [mappedExtraSchema, setMappedExtraSchema] = useState(null);
  const [numberExtraGroupOptions, setNumberExtraGroupOptions] = useState(0);
  const oldInnerValues = useRef(null);
  const {
    t
  } = useWeb5Context();
  const {
    setWithActiveAdvancedFilters
  } = useContext(FiltersContext);
  const {
    getActiveFields,
    handleOnToggleFilter,
    onToggleQuickFilter,
    renderFields,
    renderFilters,
    renderSubfilters
  } = useFilters({
    classes,
    crossOperators,
    entity,
    getAllowedOperators,
    getFieldsWithoutAdvancedFilters,
    getHasAdvancedFilters,
    isPinnable,
    onToggle,
    toggleQuickFilter,
    operators,
    schema,
    toggled,
    useSections,
    values
  });
  const hasCrossFilters = useMemo(() => (extraMultiSchema === null || extraMultiSchema === void 0 ? void 0 : extraMultiSchema.length) > 0, [extraMultiSchema]);
  const getMappedSchema = useCallback(extraSchema => {
    if (!extraSchema) return {};
    const newMappedExtraSchema = extraSchema.reduce((obj, current) => {
      const parentId = current.id;
      let children = {};
      if (current !== null && current !== void 0 && current.options) {
        var _current$options;
        setNumberExtraGroupOptions(numberExtraGroupOptions + 1);
        children = (_current$options = current.options) === null || _current$options === void 0 ? void 0 : _current$options.reduce((obj2, child) => {
          obj2[child.id] = parentId;
          return obj2;
        }, {});
      }
      obj = _objectSpread(_objectSpread({}, obj), children);
      return obj;
    }, {});
    return newMappedExtraSchema;
  }, [numberExtraGroupOptions, setNumberExtraGroupOptions]);
  useEffect(() => {
    if (!isEqual(oldInnerValues === null || oldInnerValues === void 0 ? void 0 : oldInnerValues.current, values)) {
      setInnerValues(values);
    }
  }, [values]);
  useEffect(() => {
    if (!hasCrossFilters && !mappedExtraSchema && (extraSchema === null || extraSchema === void 0 ? void 0 : extraSchema.length) > 0) {
      setMappedExtraSchema(getMappedSchema(extraSchema));
      return;
    }
    if (hasCrossFilters && !mappedExtraSchema) {
      const newMappedExtraSchema = extraMultiSchema.reduce((acc, _ref2) => {
        let {
          id,
          schema
        } = _ref2;
        acc[id] = getMappedSchema(schema);
        return acc;
      }, {});
      setMappedExtraSchema(newMappedExtraSchema);
    }
  }, [extraSchema, mappedExtraSchema, getMappedSchema, extraMultiSchema, hasCrossFilters]);
  useEffect(() => {
    if (!operators && !crossOperators) return;
    // Check if any non default advanced filter is set
    let hasActiveAdvancedFilters = false;
    const filters = [...quickSchema, ...schema].filter(Boolean);
    const checkOperators = ops => {
      for (let [key, value] of Object.entries(ops)) {
        var _filter$fields$, _fieldOperators$dataT;
        const operatorValue = value.value;
        const filter = filters.filter(item => item.id === key)[0];
        if (!filter) continue;
        const dataType = (_filter$fields$ = filter.fields[0]) === null || _filter$fields$ === void 0 ? void 0 : _filter$fields$.dataType;
        const defaultValue = dataType && ((_fieldOperators$dataT = fieldOperators[dataType]) === null || _fieldOperators$dataT === void 0 ? void 0 : _fieldOperators$dataT.default);
        if (operatorValue !== defaultValue) {
          return true;
        }
      }
    };

    // Check operators
    if (checkOperators(operators)) {
      hasActiveAdvancedFilters = true;
    }

    // Check crossOperators
    for (let key in crossOperators) {
      if (checkOperators(crossOperators[key])) {
        hasActiveAdvancedFilters = true;
      }
    }
    setWithActiveAdvancedFilters(hasActiveAdvancedFilters);
  }, [crossOperators, operators, quickSchema, schema, setWithActiveAdvancedFilters]);
  const commonOnChange = useCallback(_ref3 => {
    let {
      field,
      value,
      info,
      isGroupChange,
      crossEntity
    } = _ref3;
    let newValue = value;
    const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    const crossId = getCrossID(field.id, finalEntity);
    const advancedFilters = getHasAdvancedFilters(finalEntity);
    if (advancedFilters && field.dataType === 'probability') {
      const currentOperators = !!finalEntity ? (crossOperators === null || crossOperators === void 0 ? void 0 : crossOperators[finalEntity]) || {} : operators;
      const operatorValue = currentOperators[crossId] ? currentOperators[crossId].value : undefined;
      const oldValue = innerValues[crossId];
      let newMinValue = field.inputProps.min;
      let newMaxValue = field.inputProps.max;
      if ([LESS, LESS_OR_EQUAL].includes(operatorValue)) {
        newMinValue = Array.isArray(newValue) ? newValue[0] : newValue;
        newMaxValue = oldValue ? oldValue[1] : newMaxValue;
        newValue = [newMinValue, newMaxValue];
      }
      if ([GREATER, GREATER_OR_EQUAL].includes(operatorValue)) {
        newMinValue = oldValue ? oldValue[0] : newMinValue;
        newMaxValue = Array.isArray(newValue) ? newValue[1] : newValue;
        newValue = [newMinValue, newMaxValue];
      }
    }

    //oldInnerValues to control wether we need to reset "innerValues" or not with "values" coming from props
    //we do it before any format process because we need those values to be equal to those that come from props
    oldInnerValues.current = _objectSpread(_objectSpread({}, innerValues), {}, {
      [crossId]: newValue
    });

    // We work with "innerValues" over "values" always we can.
    // That way we prevent unnecessary rerenders.
    setInnerValues(_objectSpread(_objectSpread({}, innerValues), {}, {
      [crossId]: newValue
    }));
    if (isGroupChange) {
      if (value.filter(current => current).length > 0 && info !== null && info !== void 0 && info.action && (info === null || info === void 0 ? void 0 : info.action) === 'clear') {
        return;
      }
    }

    //Input fields only execute onChange when onBlur to make it more efficient
    if (inputFields.includes(field.dataType) && !!newValue && !(info !== null && info !== void 0 && info.action) && (info === null || info === void 0 ? void 0 : info.action) !== 'clear') {
      return;
    }
    onChange({
      filter: field,
      value: newValue,
      info,
      crossEntity: finalEntity
    });
  }, [getHasAdvancedFilters, innerValues, onChange, crossOperators, operators]);
  const handleOnChangeGroup = useCallback((field, crossEntity) => {
    const finalCrossEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    return (groupValue, value, index, event, info) => {
      commonOnChange({
        field,
        value: groupValue,
        info,
        isGroupChange: true,
        crossEntity: finalCrossEntity
      });
    };
  }, [commonOnChange]);
  const handleOnChange = useCallback(_ref4 => {
    let {
      field,
      crossEntity
    } = _ref4;
    const finalCrossEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    return (value, e, info) => {
      if (field.dataType === 'singleValueList' || field.dataType === 'multipleValueList' || field.dataType === 'relatedValueList') info = e;
      commonOnChange({
        field,
        value,
        info,
        crossEntity: finalCrossEntity
      });
    };
  }, [commonOnChange]);
  const handleOnBlur = useCallback((field, crossEntity) => {
    return value => {
      let operatorValue;
      const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
      const crossId = getCrossID(field.id, finalEntity);
      const advancedFilters = getHasAdvancedFilters(finalEntity);
      if (advancedFilters) {
        const currentOperators = !!finalEntity ? (crossOperators === null || crossOperators === void 0 ? void 0 : crossOperators[finalEntity]) || {} : operators;
        operatorValue = currentOperators[field.id] ? currentOperators[field.id].value : undefined;
      }
      if (rangeFields[field.dataType]) {
        var _innerValues$crossId;
        const innerValue = (_innerValues$crossId = innerValues[crossId]) === null || _innerValues$crossId === void 0 ? void 0 : _innerValues$crossId.filter(current => current).length;
        if (innerValue === 0 || innerValue === 2 || operatorValue && innerValue > 0 && [LESS, LESS_OR_EQUAL, GREATER, GREATER_OR_EQUAL].includes(operatorValue)) {
          return onChange({
            filter: field,
            value: innerValues[crossId] || null,
            crossEntity
          });
        }
        return;
      }
      return onChange({
        filter: field,
        value,
        crossEntity
      });
    };
  }, [getHasAdvancedFilters, onChange, crossOperators, operators, innerValues]);
  const handleChangeOperator = useCallback(_ref5 => {
    let {
      field,
      defaultValue,
      crossEntity
    } = _ref5;
    return value => {
      const newValue = value || defaultValue;
      onChangeOperator && onChangeOperator({
        filter: field,
        value: newValue,
        crossEntity
      });
      if ([EMPTY, NOT_EMPTY].includes(newValue.value)) {
        handleOnChange({
          field,
          crossEntity
        })(defaultValues[field.dataType]);
      } else if (field.dataType === 'date' && ![INSIDE_RANGE, OUTSIDE_RANGE, NO_DATA_WITHIN_RANGE].includes(newValue.value)) {
        handleOnChange({
          field,
          crossEntity
        })(defaultValues[field.dataType]);
      }
    };
  }, [handleOnChange, onChangeOperator]);
  const removeFromSelected = useCallback(filter => {
    const {
      id,
      entity: entityOverride,
      isFakeCrossFilter
    } = filter;
    const newSelected = _objectSpread({}, selectedFilters);
    const finalEntity = !!entityOverride && !isFakeCrossFilter ? entityOverride : entity.entity;
    const getGroupOptions = () => {
      if (hasCrossFilters) {
        var _newSelected$finalEnt, _newSelected$finalEnt2;
        return ((_newSelected$finalEnt = newSelected[finalEntity]) === null || _newSelected$finalEnt === void 0 ? void 0 : (_newSelected$finalEnt2 = _newSelected$finalEnt.options[mappedExtraSchema[finalEntity][id]]) === null || _newSelected$finalEnt2 === void 0 ? void 0 : _newSelected$finalEnt2.options) || [];
      } else {
        var _newSelected$mappedEx;
        return ((_newSelected$mappedEx = newSelected[mappedExtraSchema[id]]) === null || _newSelected$mappedEx === void 0 ? void 0 : _newSelected$mappedEx.options) || [];
      }
    };
    const groupOptions = getGroupOptions();
    const indexOption = groupOptions.indexOf(id);
    if (indexOption !== -1) {
      groupOptions.splice(indexOption, 1);
      if (hasCrossFilters) {
        newSelected[finalEntity].options[mappedExtraSchema[finalEntity][id]].options = groupOptions;
      } else {
        newSelected[mappedExtraSchema[id]].options = groupOptions;
      }
      onChangeSelectedFilters(newSelected);
    }
  }, [selectedFilters, entity, hasCrossFilters, mappedExtraSchema, onChangeSelectedFilters]);
  const handleOnDelete = useCallback(filter => {
    return () => {
      const newInnerValues = _objectSpread({}, innerValues);
      const {
        isFakeCrossFilter,
        crossId,
        id
      } = filter;
      const ID = !!crossId && !isFakeCrossFilter ? crossId : id;
      delete newInnerValues[ID];
      setInnerValues(newInnerValues);
      removeFromSelected(filter);
      onDelete && onDelete(crossId || id);
    };
  }, [onDelete, innerValues, removeFromSelected]);
  const renderFieldsProps = useMemo(() => ({
    handleChangeOperator,
    handleOnBlur,
    handleOnChange,
    handleOnChangeGroup,
    innerValues
  }), [handleChangeOperator, handleOnBlur, handleOnChange, handleOnChangeGroup, innerValues]);
  const renderFiltersProps = useMemo(() => ({
    handleChangeOperator,
    handleOnBlur,
    handleOnChange,
    handleOnChangeGroup,
    handleOnDelete,
    innerValues
  }), [handleChangeOperator, handleOnBlur, handleOnChange, handleOnChangeGroup, handleOnDelete, innerValues]);
  const onDragStart = useCallback(result => {
    onDrag && onDrag(result);
  }, [onDrag]);
  const onDragEnd = useCallback(result => {
    onDrop && onDrop(result);

    // dropped outside the list
    if (!result.destination) {
      return;
    }
    onReOrder && onReOrder(result.source.index, result.destination.index);
  }, [onDrop, onReOrder]);
  const getQuickFilterPin = useCallback(filter => {
    if (filter.hasOwnProperty('isPinnable') && !filter.isPinnable) return null;
    const ID = filter.crossId || filter.id;
    return /*#__PURE__*/React.createElement(Tooltip, {
      content: t('label_filters_unpin_desc'),
      placement: "top"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
      className: classes.iconSection,
      name: "unpin",
      onClick: onToggleQuickFilter(ID)
    })));
  }, [classes.iconSection, onToggleQuickFilter, t]);
  const renderQuickFilters = useMemo(() => {
    if (!(quickSchema !== null && quickSchema !== void 0 && quickSchema.length)) return;
    return /*#__PURE__*/React.createElement(FiltersSection, {
      title: t('label_filters_section_quickfilters'),
      filterNum: quickSchema === null || quickSchema === void 0 ? void 0 : quickSchema.length
    }, /*#__PURE__*/React.createElement(DragDropContext, {
      onDragStart: onDragStart,
      onDragEnd: onDragEnd
    }, /*#__PURE__*/React.createElement(Droppable, {
      droppableId: "droppableQuickFilters"
    }, provided => /*#__PURE__*/React.createElement("div", _extends({}, provided.droppableProps, {
      ref: provided.innerRef
    }), (quickSchema === null || quickSchema === void 0 ? void 0 : quickSchema.length) > 0 && quickSchema.map((filter, index) => {
      const ID = filter.crossId || filter.id;
      const sectionClasses = classnames(classes.filter, `section-filter__field__${ID}`, {
        [classes.crossFilter]: !!filter.crossId
      });
      const title = filter.label && filter.subLabel ? /*#__PURE__*/React.createElement("div", {
        className: classes.labelMultiline
      }, /*#__PURE__*/React.createElement(Text, {
        type: "subtitle",
        medium: true
      }, filter.label), /*#__PURE__*/React.createElement(Text, {
        type: "caption"
      }, filter.subLabel)) : filter.label;
      const hasSubfilters = !!filter.subfilterSchema;
      let isOpen = false;
      if (toggled.hasOwnProperty(ID)) {
        const toggledFilter = toggled[ID];
        isOpen = typeof toggledFilter === 'boolean' ? toggledFilter : toggledFilter.open;
      }
      return /*#__PURE__*/React.createElement(Draggable, {
        key: ID,
        draggableId: ID,
        index: index,
        isDragDisabled: quickSchema.length === 1
      }, (provided, snapshot) => {
        var _toggled$ID;
        return /*#__PURE__*/React.createElement("div", _extends({
          ref: provided.innerRef
        }, provided.draggableProps, {
          style: getItemStyle(snapshot.isDragging, provided.draggableProps.style)
        }), /*#__PURE__*/React.createElement(Section, {
          key: index,
          className: sectionClasses,
          title: title,
          isOpen: isOpen,
          onChange: handleOnToggleFilter({
            filter: ID,
            hasSubfilters
          }),
          activeFields: getActiveFields(filter),
          headerPreComponent: quickSchema.length > 1 && /*#__PURE__*/React.createElement("span", provided.dragHandleProps, /*#__PURE__*/React.createElement(Icon, {
            className: classnames(classes.iconSection, classes.iconDrag),
            name: "dragAndDrop"
          })),
          headerPostComponent: getQuickFilterPin(filter),
          onRemove: filter.isRemovable ? handleOnDelete(filter) : undefined,
          classes: {
            header: classes.filterHeader,
            open: isOpen ? classes.filterHeaderOpen : null
          }
        }, renderFields(_objectSpread(_objectSpread({}, renderFieldsProps), {}, {
          crossEntity: filter.entity,
          fields: filter.fields
        })), renderSubfilters(_objectSpread(_objectSpread({}, renderFiltersProps), {}, {
          className: classes.subFilters,
          parentFilter: ID,
          renderer: renderFilters,
          schema: filter.subfilterSchema,
          toggled: ((_toggled$ID = toggled[ID]) === null || _toggled$ID === void 0 ? void 0 : _toggled$ID.subfilters) || {}
        }))));
      });
    }), provided.placeholder))));
  }, [quickSchema, t, onDragStart, onDragEnd, classes.filter, classes.crossFilter, classes.iconSection, classes.iconDrag, classes.filterHeader, classes.filterHeaderOpen, classes.subFilters, classes.labelMultiline, toggled, handleOnToggleFilter, getActiveFields, getQuickFilterPin, handleOnDelete, renderFields, renderFieldsProps, renderSubfilters, renderFiltersProps, renderFilters]);
  const renderCrossFilters = useMemo(() => {
    if (!crossSchema || !Object.entries(crossSchema).length) return;
    return Object.entries(crossSchema).map(_ref6 => {
      let [key, schema] = _ref6;
      return /*#__PURE__*/React.createElement(Fragment, {
        key: key
      }, renderFilters(_objectSpread(_objectSpread({}, renderFiltersProps), schema)));
    });
  }, [crossSchema, renderFilters, renderFiltersProps]);
  const renderFiltersSections = useMemo(() => {
    const showGeneralFilters = (quickSchema === null || quickSchema === void 0 ? void 0 : quickSchema.length) > 0 || (schema === null || schema === void 0 ? void 0 : schema.length) > 0;
    const overrideTitle = {
      companies: t('label_filters_cross_filters_section_account'),
      contacts: t('label_filters_cross_filters_section_contact'),
      activities: t('label_filters_cross_filters_section_activity'),
      opportunities: t('label_filters_cross_filters_section_opportunity'),
      salesOrders: t('label_filters_cross_filters_section_salesorders')
    }[entity === null || entity === void 0 ? void 0 : entity.entity];
    return crossSchema && Object.entries(crossSchema).length ? /*#__PURE__*/React.createElement(Fragment, null, useGeneralSections && /*#__PURE__*/React.createElement(Fragment, null, showGeneralFilters && /*#__PURE__*/React.createElement(FiltersSection, {
      title: t('label_general_filters')
    }, !avoidQuickFilters && renderQuickFilters, renderFilters(_objectSpread(_objectSpread({}, renderFiltersProps), {}, {
      title: overrideTitle
    }))), /*#__PURE__*/React.createElement(FiltersSection, {
      title: t('label_related_entities'),
      hint: {
        title: t('label_cross_filters_info'),
        description: t('label_cross_filters_info_desc', [t('action_cross_filters_info_link')], text => /*#__PURE__*/React.createElement(Link, {
          href: t('label_cross_filters_info_link'),
          target: "_blank"
        }, text)),
        new: t('label_new_male')
      }
    }, renderCrossFilters)), !useGeneralSections && /*#__PURE__*/React.createElement(Fragment, null, !avoidQuickFilters && renderQuickFilters, renderFilters(_objectSpread(_objectSpread({}, renderFiltersProps), {}, {
      title: overrideTitle
    })), renderCrossFilters)) : /*#__PURE__*/React.createElement(Fragment, null, !useSectionsTitle && /*#__PURE__*/React.createElement(Fragment, null, !avoidQuickFilters && renderQuickFilters, renderFilters(renderFiltersProps)), useSectionsTitle && /*#__PURE__*/React.createElement(Fragment, null, !avoidQuickFilters && renderQuickFilters, renderFilters(_objectSpread(_objectSpread({}, renderFiltersProps), {}, {
      title: overrideTitle
    }))));
  }, [quickSchema === null || quickSchema === void 0 ? void 0 : quickSchema.length, schema === null || schema === void 0 ? void 0 : schema.length, t, entity === null || entity === void 0 ? void 0 : entity.entity, crossSchema, renderQuickFilters, renderFilters, renderFiltersProps, renderCrossFilters, useGeneralSections, useSectionsTitle, avoidQuickFilters]);
  const isEmpty = useMemo(() => {
    const hasQuickFilters = !!(quickSchema !== null && quickSchema !== void 0 && quickSchema.length) || false;
    const hasGeneralFilters = !!(schema !== null && schema !== void 0 && schema.length) || false;
    const hasCrossFilters = crossSchema && !!Object.entries(crossSchema).length || false;
    if (!hasQuickFilters && !hasGeneralFilters && !hasCrossFilters) return true;
    return false;
  }, [quickSchema, schema, crossSchema]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(classes.filtersWrapper, {
      [classes.withHeader]: useHeader && !showAdd,
      [classes.withAddButton]: showAdd && !useHeader,
      [classes.withHeaderAndAddButton]: useHeader && showAdd
    })
  }, (useViewsInFilters || useViewsInMenu) && /*#__PURE__*/React.createElement(FilterViews, {
    labels: labelsViews,
    labelsSave: labelsSaveViewsModal,
    labelsDelete: labelsDeleteViewsModal,
    isOpen: isOpenViewsModal,
    onToggleSaveViewsModal: onToggleSaveViewsModal,
    useViewsInFilters: useViewsInFilters,
    useViewsInMenu: useViewsInMenu,
    views: views,
    selectedView: selectedView,
    customView: customView,
    onChange: onChangeView,
    onChangeCustomView: onChangeCustomView,
    onSave: onSaveView,
    onDelete: onDeleteView,
    onDeleteCustomView: onDeleteCustomView,
    handleOnToggleFilter: handleOnToggleFilter,
    toggled: toggled,
    canManagePublicViews: canManagePublicViews,
    canManagePrivateViews: canManagePrivateViews
  }), renderFiltersSections, isEmpty && _ref7), showAdd && /*#__PURE__*/React.createElement("div", {
    className: classes.addButton
  }, /*#__PURE__*/React.createElement(Button, {
    isFullWidth: true,
    icon: "plus",
    type: "secondary",
    onClick: onToggleAddModal,
    isDisabled: numberExtraGroupOptions === 0
  }, (labels === null || labels === void 0 ? void 0 : labels.addFields) || t('label_add_filters'))), /*#__PURE__*/React.createElement(ModalOptions, modalOptionsProps));
});
process.env.NODE_ENV !== "production" ? Content.propTypes = {
  showAdd: PropTypes.bool,
  schema: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      id: PropTypes.string,
      dataType: PropTypes.string,
      labelMode: PropTypes.oneOf(['vertical', 'horizontal']),
      inputAttrs: PropTypes.shape({
        placeholder: PropTypes.string
      })
    })),
    isRemovable: PropTypes.bool
  })),
  values: PropTypes.object,
  onToggle: PropTypes.func,
  toggled: PropTypes.object,
  handleOnAdd: PropTypes.func,
  labels: PropTypes.shape({
    deleteField: PropTypes.string,
    addFields: PropTypes.string
  }),
  labelsViews: PropTypes.shape({
    views: PropTypes.string,
    viewsPlaceholder: PropTypes.string,
    viewsNoOptionsPlaceholder: PropTypes.string,
    viewsLoadingPlaceholder: PropTypes.string
  }),
  labelsSaveViewsModal: PropTypes.shape({
    title: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    viewName: PropTypes.string,
    saveAs: PropTypes.string,
    viewNamePlaceholder: PropTypes.string,
    saveAsPlaceholder: PropTypes.string
  }),
  labelsDeleteViewsModal: PropTypes.shape({
    title: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    questionText: PropTypes.string,
    adviceText: PropTypes.string,
    confirmWriteText: PropTypes.string,
    confirmWord: PropTypes.string,
    confirmWritePlaceholder: PropTypes.string
  }),
  operators: PropTypes.object,
  onChangeOperator: PropTypes.func
} : void 0;
export default Content;